import { ArrowLeft, CalendarDays, FilePenLine, Star } from "lucide-react";
import { Link, Navigate, useParams } from "react-router-dom";

import Loading from "../components/common/LoaderCommon";
import {
  type WarrantyClaimApi,
  type WarrantyDetailResponse,
  type WarrantyProductApi,
  useGetWarrantyClaim,
  useGetWarrantyDetail,
} from "../service/warrantyService";
import { Button } from "../utils/components/ui/button";
import type {
  ClaimStatus,
  WarrantyProduct,
  WarrantyCoverageStatus,
} from "./warranty/warrantyShared";
import {
  formatWarrantyDate,
  getClaimStatusClasses,
  WarrantyAttachmentChip,
  WarrantyProductArtwork,
  WarrantyProductSummary,
} from "./warranty/warrantyShared";

const toNumberValue = (value: unknown) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsedValue = Number(value);

    if (Number.isFinite(parsedValue)) {
      return parsedValue;
    }
  }

  return 0;
};

const toClaimStatus = (value: unknown): ClaimStatus | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }

  const normalizedValue = value.trim().toLowerCase();

  if (normalizedValue.includes("open")) {
    return "Open";
  }

  if (
    normalizedValue.includes("closed") ||
    normalizedValue.includes("close") ||
    normalizedValue.includes("resolved")
  ) {
    return "Closed";
  }

  return undefined;
};

const toCoverageStatus = (value: unknown): WarrantyCoverageStatus => {
  if (typeof value === "string" && value.trim().toLowerCase() === "expired") {
    return "Expired";
  }

  return "Active";
};

type ClaimViewProduct = WarrantyProduct & { claimId?: string };

type ClaimViewInvoice = {
  salesId: string;
  invoiceNumber: string;
  customerName: string;
  purchaseDate: string;
  products: ClaimViewProduct[];
};

const normalizeWarrantyProduct = (
  product: WarrantyProductApi,
): ClaimViewProduct => ({
  id: String(product.salesProductId),
  name: product.productName,
  brand: product.brandName,
  warrantyStatus: toCoverageStatus(product.warrantyStatus),
  expiryDate: product.warrantyExpiryDate,
  price: toNumberValue(product.sellingPrice),
  originalPrice:
    toNumberValue(product.mrp) || toNumberValue(product.sellingPrice),
  claimId: product.claims[0] ? String(product.claims[0].claimId) : undefined,
});

const normalizeWarrantyInvoice = (
  item?: WarrantyDetailResponse,
): ClaimViewInvoice | null => {
  if (!item) {
    return null;
  }

  return {
    salesId: String(item.salesId),
    invoiceNumber: item.invoiceNumber,
    customerName: item.customerName,
    purchaseDate: item.purchaseDate,
    products: item.products.map(normalizeWarrantyProduct),
  };
};

const normalizeClaim = (claim?: WarrantyClaimApi) => {
  if (!claim) {
    return undefined;
  }

  const status = toClaimStatus(claim.status);

  if (!status) {
    return undefined;
  }

  return {
    claimId: String(claim.claimId),
    claimDate: claim.claimDate,
    status,
    issueDescription: claim.issueDescription,
    resolutionNotes: claim.resolutionNotes,
    attachmentName: claim.attachment || "Attachment",
    attachmentSize: "",
  };
};

function WarrantyClaimView() {
  const { salesId, claimId } = useParams();

  const { data: invoiceData, isLoading: isDetailLoading } =
    useGetWarrantyDetail(salesId);
  const { data: claimData, isLoading: isClaimLoading } =
    useGetWarrantyClaim(claimId);

  const invoice = normalizeWarrantyInvoice(invoiceData);
  const claim = normalizeClaim(claimData);
  const product = invoice?.products.find((item) => item.claimId === claimId);

  if (isDetailLoading || isClaimLoading) {
    return <Loading />;
  }

  if (!invoice || !product || !claim) {
    return <Navigate to="/warranty" replace />;
  }

  return (
    <div className="flex min-h-full flex-col gap-6 pb-4">
      <section className="rounded-[28px] border border-[#DDE7FB] bg-[radial-gradient(circle_at_top_left,rgba(26,94,249,0.12),transparent_28%),linear-gradient(180deg,#FFFFFF_0%,#F5F9FF_100%)] p-6 shadow-[0_18px_50px_-30px_rgba(15,23,42,0.35)] sm:p-8">
        <div className="space-y-4 space-x-4">
          <Link
            to={`/warranty/${invoice.salesId}`}
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition hover:text-slate-700"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to warranty detail
          </Link>
          <div className="inline-flex items-center rounded-full border border-[#CFE0FF] bg-[#EEF4FF] px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-[#1A5EF9]">
            {invoice.invoiceNumber}
          </div>
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              Warranty Claim
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-500 sm:text-base">
              Review claim status, issue details, and service notes for this
              protected product.
            </p>
          </div>
        </div>
      </section>

      <div className="rounded-[28px] border border-[#DDE7FB] bg-white p-6 shadow-[0_18px_40px_-32px_rgba(15,23,42,0.45)]">
        <div className="mb-6 grid gap-4 sm:grid-cols-2">
          <div className="rounded-[22px] border border-[#E8EDF3] bg-[#F8FAFC] p-4">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
              Customer Name
            </p>
            <p className="mt-2 text-lg font-semibold text-slate-900">
              {invoice.customerName}
            </p>
          </div>
          <div className="rounded-[22px] border border-[#E8EDF3] bg-[#F8FAFC] p-4">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
              Purchase Date
            </p>
            <p className="mt-2 text-lg font-semibold text-slate-900">
              {formatWarrantyDate(invoice.purchaseDate)}
            </p>
          </div>
        </div>

        <div className="space-y-5">
          {invoice.products.map((item) => (
            <div
              key={item.id}
              className="rounded-3xl border border-[#DDE7FB] bg-[linear-gradient(180deg,#FFFFFF_0%,#F7FAFF_100%)] p-5"
            >
              <div className="flex flex-col gap-5 md:flex-row md:items-start">
                <WarrantyProductArtwork />
                <div className="flex-1">
                  <WarrantyProductSummary product={item} />

                  {item.id === product.id && (
                    <div className="mt-5 space-y-4 rounded-[20px] border border-[#E8EDF3] bg-white p-4">
                      <div className="flex flex-col gap-3 border-b border-[#EEF2F6] pb-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="space-y-2 text-sm text-slate-500">
                          <p className="flex items-center gap-2">
                            <CalendarDays className="h-4 w-4 text-slate-400" />
                            Claim Date: {formatWarrantyDate(claim.claimDate)}
                          </p>
                          <p className="flex items-center gap-2">
                            <Star className="h-4 w-4 text-slate-400" />
                            Status:
                            <span
                              className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium uppercase tracking-wide ${getClaimStatusClasses(
                                claim.status,
                              )}`}
                            >
                              {claim.status}
                            </span>
                          </p>
                        </div>
                        <Button
                          asChild
                          variant="link"
                          className="h-auto justify-start px-0 text-[#1A5EF9] hover:text-[#0E4FDE]"
                        >
                          <Link
                            to={`/warranty/${invoice.salesId}/claim/${claim.claimId}/edit`}
                          >
                            <FilePenLine className="h-4 w-4" />
                            Update Details
                          </Link>
                        </Button>
                      </div>

                      <div className="space-y-4 text-sm text-slate-600">
                        <div>
                          <p className="font-semibold text-slate-900">
                            Issue Description
                          </p>
                          <p className="mt-1">{claim.issueDescription}</p>
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">
                            Resolution Notes
                          </p>
                          <p className="mt-1">
                            {claim.resolutionNotes || "------"}
                          </p>
                        </div>
                        <WarrantyAttachmentChip
                          attachmentName={claim.attachmentName}
                          attachmentSize={claim.attachmentSize}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default WarrantyClaimView;
